import React, { useState } from 'react';
import { Container, Grid, Button, Typography } from '@mui/material';
import Page from 'src/components/Page';
import { useNavigate, useParams } from 'react-router-dom';
import { TextField } from 'src/components';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BadgeImage1 } from 'src/images';
import { badgeService } from 'src/services/badge.service';
import { badgeValidationSchema } from 'src/validations/admin';
import { UpdateBadgeDTO } from 'src/types/teacher.types';
import { IoImagesOutline } from 'react-icons/io5';

import '../../styles/addstudent.css';
import 'react-toastify/dist/ReactToastify.css';

export default function EditBadge() {
  const navigate = useNavigate();
  const { badgeId: id } = useParams<{ badgeId: string }>();
  const queryClient = useQueryClient();
  const [fileError, setFileError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const { data: badge, isLoading: isBadgeLoading } = useQuery({
    queryKey: ['badge', id],
    queryFn: () => badgeService.getBadgeById(id!),
    enabled: !!id,
  });

  const mutation = useMutation({
    mutationFn: async (values: UpdateBadgeDTO) => {
      setLoading(true);
      try {
        // First update the badge details
        const updatedBadge = await badgeService.updateBadge(id!, {
          name: values.name,
          description: values.description,
          points: Number(values.points),
        });

        return updatedBadge;
      } finally {
        setLoading(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['badges'] });
      queryClient.invalidateQueries({ queryKey: ['badge', id] });
      toast.success('Badge updated successfully');
      // navigate('/admin/badges');
    },
    onError: (error: Error) => {
      console.error('Update badge error:', error);
      toast.error('Failed to update badge');
    },
  });

  const handleUploadImage = async (file: File) => {
    if (!file || !id) return;

    try {
      setLoading(true);

      const publicUrl = await badgeService.updateBadgeImage(id, file);

      formik.setFieldValue('photourl', publicUrl);
      // Invalidate both the specific teacher and teachers list queries
      queryClient.invalidateQueries({ queryKey: ['badge', id] });
      queryClient.invalidateQueries({ queryKey: ['badges'] });
    } catch (error) {
      toast.error('Failed to update profile picture');
    } finally {
      setLoading(false);
    }
  };

  const handleBadgeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      handleUploadImage(event.target.files[0]);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: badge?.name || '',
      description: badge?.description || '',
      points: badge?.points || '',
    },
    validationSchema: badgeValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!values.name || !values.description) {
        toast.error('Please fill in all required fields');
        return;
      }

      // Check if either a new image is selected or an existing image is present
      if (!selectedFile && !badge?.imageUrl) {
        toast.error('Please select a badge image');
        return;
      }

      try {
        await mutation.mutateAsync(values);
      } catch (error) {
        console.error('Error updating badge:', error);
      }
    },
  });

  const handleDeletePhoto = async () => {
    try {
      await badgeService.deleteBadgeImage(id!);
      queryClient.invalidateQueries({ queryKey: ['badge', id] });
      queryClient.invalidateQueries({ queryKey: ['badges'] });
    } catch (error) {
      console.error('Error deleting badge image:', error);
      toast.error('Failed to delete badge image');
    }
  };

  if (isBadgeLoading) {
    return <Typography>Loading...</Typography>;
  }

  if (!badge) {
    return <Typography>Badge not found</Typography>;
  }

  return (
    <Page title="Edit Badge">
      <Container className="_container" maxWidth="xl">
        <Typography variant="h4">Edit Badge</Typography>
        <div className="_divider"></div>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={2} md={1} lg={1}>
            {selectedFile ? (
              <img
                src={URL.createObjectURL(selectedFile)}
                alt="Selected Badge"
                style={{ width: '100%', height: 100, borderRadius: '8px' }}
              />
            ) : badge.imageUrl ? (
              <img src={badge.imageUrl} alt={badge.name} style={{ width: '100%', height: 100, borderRadius: '8px' }} />
            ) : (
              <div
                style={{
                  height: 100,
                  width: 100,
                  borderRadius: '8px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                }}
              >
                <IoImagesOutline size={40} />
              </div>
            )}
          </Grid>
          <Grid item xs={12} sm={10} md={10} lg={10}>
            <Typography variant="subtitle1" sx={{ color: 'grey.400', fontWeight: 500 }}>
              Only support .JPG, .JPEG, or .PNG file.
            </Typography>

            <div className="upload_row" style={{ marginTop: 0 }}>
              <input
                type="file"
                id="_upload_pic"
                onChange={handleBadgeChange}
                accept="image/jpeg,image/png,image/jpg"
                required={!badge?.imageUrl}
              />
              <label className="_btn" htmlFor="_upload_pic">
                {loading ? 'Uploading...' : 'Upload  photo'}
              </label>
              {selectedFile && (
                <button
                  type="button"
                  className="_btn"
                  style={{ backgroundColor: 'var(--white)', color: 'var(--primary-main)' }}
                  onClick={() => {
                    setSelectedFile(null);
                    setFileError('');
                    const fileInput = document.getElementById('_upload_pic') as HTMLInputElement;
                    if (fileInput) {
                      fileInput.value = '';
                    }
                  }}
                >
                  Delete Photo
                </button>
              )}
              {badge?.imageUrl && !selectedFile && (
                <button
                  type="button"
                  className="_btn"
                  style={{ backgroundColor: 'var(--white)', color: 'var(--primary-main)' }}
                  onClick={handleDeletePhoto}
                  disabled={loading}
                >
                  Delete Photo
                </button>
              )}
            </div>

            {fileError && (
              <Typography style={{ fontSize: 12, fontWeight: 700, marginTop: 10 }} color="error">
                {fileError}
              </Typography>
            )}
          </Grid>
        </Grid>
        <br />

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Badge Name"
                placeholder="Enter Badge Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                name="name"
                error={Boolean(formik.touched.name && formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Points"
                placeholder="Enter Points"
                type="number"
                value={formik.values.points}
                onChange={formik.handleChange}
                name="points"
                error={Boolean(formik.touched.points && formik.errors.points)}
                helperText={formik.touched.points && formik.errors.points}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                placeholder="Description"
                value={formik.values.description}
                onChange={formik.handleChange}
                name="description"
                multiline
                rows={4}
                error={Boolean(formik.touched.description && formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
                required
              />
            </Grid>
          </Grid>
          <div className="_add_st_footer_row" style={{ marginTop: 15 }}>
            <Button
              variant="outlined"
              style={{ height: 48, color: 'var(--primary-main)', width: 120, margin: 5 }}
              onClick={() => navigate('/admin/badges')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              style={{ height: 48, width: 120, margin: 5 }}
              disabled={loading || mutation.isPending}
            >
              {loading || mutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </Container>
    </Page>
  );
}
